import { useEffect, useRef, useState } from 'react';

interface BlurTextareaProps
  extends Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    'value' | 'defaultValue' | 'onChange'
  > {
  /** The persisted value, shown whenever the field is not being edited. */
  value: string;
  /**
   * Called with the final text when the field loses focus (or unmounts mid-edit) and the text actually
   * changed. This — not every keystroke — is what should write the store / trigger a save.
   */
  onCommit: (value: string) => void;
}

/**
 * A textarea that stays responsive under fast typing.
 *
 * Keystrokes update only local component state; the value is pushed to the caller (`onCommit`) when
 * the field loses focus, or if it unmounts while still being edited (navigating away / a step
 * remount). Binding a textarea's `value`/`onChange` straight to the workshop store instead wrote the
 * whole store on every character — re-rendering the entire step and resetting the debounced save each
 * keystroke — which made quick typing feel painfully unresponsive.
 *
 * An external change to `value` (a different delivery/workshop, a server reconcile) is adopted only
 * while the field is not focused, so it can never clobber what the SE is in the middle of typing.
 */
export function BlurTextarea({ value, onCommit, onFocus, onBlur, ...rest }: BlurTextareaProps) {
  const [draft, setDraft] = useState(value);
  const focused = useRef(false);
  // Refs so the unmount-commit effect (which runs once) always sees the latest text, persisted value,
  // and callback rather than the ones captured at mount.
  const draftRef = useRef(value);
  const valueRef = useRef(value);
  const commitRef = useRef(onCommit);
  draftRef.current = draft;
  valueRef.current = value;
  commitRef.current = onCommit;

  useEffect(() => {
    if (!focused.current) setDraft(value);
  }, [value]);

  const commit = () => {
    if (draftRef.current !== valueRef.current) commitRef.current(draftRef.current);
  };

  useEffect(
    () => () => {
      if (focused.current) commit();
    },
    // Mount/unmount only; `commit` reads everything it needs through refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <textarea
      {...rest}
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onFocus={(e) => {
        focused.current = true;
        onFocus?.(e);
      }}
      onBlur={(e) => {
        focused.current = false;
        commit();
        onBlur?.(e);
      }}
    />
  );
}
